import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getArticleBySlug, getRelatedArticles, articles } from '@/mocks/articles';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { SEOHelmet } from '@/components/SEOHelmet';
import { SITE_URL } from '@/config/site';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const article = getArticleBySlug(slug || '');
  const lang = i18n.language;

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">Article Not Found</h1>
            <a href="/" className="text-primary-600 hover:text-primary-700">{t('back_to_home')}</a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const title = lang === 'pt' ? article.titlePt : lang === 'de' ? article.titleDe : article.title;
  const description = lang === 'pt' ? article.descriptionPt : lang === 'de' ? article.descriptionDe : article.description;
  const content = lang === 'pt' ? article.contentPt : lang === 'de' ? article.contentDe : article.content;
  const toc = lang === 'pt' ? article.tocPt : lang === 'de' ? article.tocDe : article.toc;
  const quickAnswer = lang === 'pt' ? article.quickAnswerPt : lang === 'de' ? article.quickAnswerDe : article.quickAnswer;
  const pros = lang === 'pt' ? article.prosPt : lang === 'de' ? article.prosDe : article.pros;
  const cons = lang === 'pt' ? article.consPt : lang === 'de' ? article.consDe : article.cons;
  const checklist = lang === 'pt' ? article.checklistPt : lang === 'de' ? article.checklistDe : article.checklist;
  const faq = lang === 'pt' ? article.faqPt : lang === 'de' ? article.faqDe : article.faq;

  const related = getRelatedArticles(article.relatedArticles);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Simple markdown parser for content
  const renderContent = (text: string) => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let currentList: string[] = [];
    let inList = false;

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      if (trimmed.startsWith('## ')) {
        if (inList && currentList.length > 0) {
          elements.push(
            <ul key={`ul-${index}`} className="list-disc list-inside space-y-2 text-foreground-700 mb-6">
              {currentList.map((item, i) => (
                <li key={i} className="text-sm leading-relaxed">{item}</li>
              ))}
            </ul>
          );
          currentList = [];
          inList = false;
        }
        const id = trimmed.replace('## ', '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '');
        elements.push(
          <h2 key={index} id={id} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
            {trimmed.replace('## ', '')}
          </h2>
        );
      } else if (trimmed.startsWith('**') && trimmed.endsWith('**') && !trimmed.includes(': ')) {
        if (inList && currentList.length > 0) {
          elements.push(
            <ul key={`ul-${index}`} className="list-disc list-inside space-y-2 text-foreground-700 mb-6">
              {currentList.map((item, i) => (
                <li key={i} className="text-sm leading-relaxed">{item}</li>
              ))}
            </ul>
          );
          currentList = [];
          inList = false;
        }
        elements.push(
          <p key={index} className="text-sm font-semibold text-foreground-800 mb-2">
            {trimmed.replace(/\*\*/g, '')}
          </p>
        );
      } else if (trimmed.startsWith('- ') || trimmed.startsWith('1. ') || trimmed.startsWith('2. ') || trimmed.startsWith('3. ') || trimmed.startsWith('4. ') || trimmed.startsWith('5. ') || trimmed.startsWith('6. ') || trimmed.startsWith('7. ')) {
        currentList.push(trimmed.replace(/^-?\s*\d*\.?\s*/, ''));
        inList = true;
      } else if (trimmed === '') {
        if (inList && currentList.length > 0) {
          elements.push(
            <ul key={`ul-${index}`} className="list-disc list-inside space-y-2 text-foreground-700 mb-6">
              {currentList.map((item, i) => (
                <li key={i} className="text-sm leading-relaxed">{item}</li>
              ))}
            </ul>
          );
          currentList = [];
          inList = false;
        }
      } else if (trimmed) {
        if (inList && currentList.length > 0) {
          elements.push(
            <ul key={`ul-${index}`} className="list-disc list-inside space-y-2 text-foreground-700 mb-6">
              {currentList.map((item, i) => (
                <li key={i} className="text-sm leading-relaxed">{item}</li>
              ))}
            </ul>
          );
          currentList = [];
          inList = false;
        }
        elements.push(
          <p key={index} className="text-sm text-foreground-700 leading-relaxed mb-4">
            {trimmed}
          </p>
        );
      }
    });

    if (inList && currentList.length > 0) {
      elements.push(
        <ul key="ul-final" className="list-disc list-inside space-y-2 text-foreground-700 mb-6">
          {currentList.map((item, i) => (
            <li key={i} className="text-sm leading-relaxed">{item}</li>
          ))}
        </ul>
      );
    }

    return elements;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHelmet
        title={title}
        description={description}
        keywords={article.keywords}
        pathname={`/article/${article.slug}`}
        lang={lang}
        articleSchema
        faqSchema={faq}
        breadcrumbSchema={[
          { name: t('breadcrumb_home'), url: `${SITE_URL}/` },
          { name: t('breadcrumb_guides'), url: `${SITE_URL}/guides` },
          { name: title, url: `${SITE_URL}/article/${article.slug}` },
        ]}
      />
      <Navbar />
      <main className="flex-1 pt-16">
        {/* Article Hero */}
        <section className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-8 py-10 md:py-16 max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
              <a href="/" className="hover:text-primary-600 transition-colors">{t('breadcrumb_home')}</a>
              <i className="ri-arrow-right-s-line" />
              <a href="/guides" className="hover:text-primary-600 transition-colors">{t('breadcrumb_guides')}</a>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-700">{title}</span>
            </nav>

            <div className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
              <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded-md font-medium">{article.category}</span>
              <span>{article.updatedDate}</span>
              <span>·</span>
              <span>{article.readingTime} {t('reading_time')}</span>
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {title}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed mb-6">
              {description}
            </p>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary-200 flex items-center justify-center">
                  <i className="ri-user-line text-primary-700 text-sm" />
                </div>
                <span className="text-sm font-medium text-foreground-700">{article.author}</span>
              </div>
              <span className="text-xs text-foreground-400">{t('updated')}: {article.updatedDate}</span>
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-8 py-8 md:py-12 max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8">
            {/* Main Content */}
            <div>
              {/* Quick Answer */}
              <div className="bg-accent-50 border border-accent-200 rounded-xl p-5 mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-lightbulb-line text-accent-600" />
                  <h3 className="font-heading text-sm font-semibold text-accent-900">{t('article_quick_answer')}</h3>
                </div>
                <p className="text-sm text-accent-800 leading-relaxed">{quickAnswer}</p>
              </div>

              {/* Table of Contents */}
              <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 mb-8">
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-4">{t('article_toc')}</h3>
                <nav className="space-y-2">
                  {toc.map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className="block text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                    >
                      {item.title}
                    </a>
                  ))}
                </nav>
              </div>

              {/* Article Content */}
              <article className="prose prose-sm max-w-none">
                {renderContent(content)}
              </article>

              {/* Pros/Cons */}
              <div className="grid md:grid-cols-2 gap-4 mt-10">
                <div className="bg-primary-50 border border-primary-200 rounded-xl p-5">
                  <h3 className="font-heading text-sm font-semibold text-primary-900 mb-4 flex items-center gap-2">
                    <i className="ri-check-line" />
                    {t('article_pros')}
                  </h3>
                  <ul className="space-y-2">
                    {pros.map((p, i) => (
                      <li key={i} className="text-sm text-primary-800 flex items-start gap-2">
                        <i className="ri-check-fill text-primary-500 mt-0.5 flex-shrink-0 text-xs" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-secondary-50 border border-secondary-200 rounded-xl p-5">
                  <h3 className="font-heading text-sm font-semibold text-secondary-900 mb-4 flex items-center gap-2">
                    <i className="ri-close-line" />
                    {t('article_cons')}
                  </h3>
                  <ul className="space-y-2">
                    {cons.map((c, i) => (
                      <li key={i} className="text-sm text-secondary-800 flex items-start gap-2">
                        <i className="ri-close-fill text-secondary-500 mt-0.5 flex-shrink-0 text-xs" />
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Checklist */}
              <div className="mt-10 bg-background-100 border border-background-200/70 rounded-xl p-5">
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                  <i className="ri-task-line" />
                  {t('article_checklist')}
                </h3>
                <ul className="space-y-3">
                  {checklist.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded border border-foreground-300 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-foreground-500 text-xs" />
                      </div>
                      <span className="text-sm text-foreground-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* FAQ Section */}
              <div className="mt-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                  {t('faq_title')}
                </h2>
                <div className="space-y-3">
                  {faq.map((item, i) => (
                    <div key={i} className="bg-background-100 border border-background-200/70 rounded-xl p-5">
                      <h3 className="font-medium text-sm text-foreground-800 mb-2">{item.question}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="mt-10 bg-primary-500 rounded-xl p-6 md:p-8 text-center">
                <h3 className="font-heading text-lg font-bold text-background-50 mb-2">
                  {t('final_cta_title')}
                </h3>
                <p className="text-sm text-background-200/90 mb-5">
                  {t('final_cta_subtitle')}
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 text-primary-700 font-medium rounded-lg hover:bg-background-100 transition-all text-sm"
                >
                  {t('hero_cta_primary')}
                  <i className="ri-arrow-right-line" />
                </a>
              </div>

              {/* Back to Home */}
              <div className="mt-8 text-center">
                <a href="/" className="text-sm text-primary-600 hover:text-primary-700 transition-colors inline-flex items-center gap-1">
                  <i className="ri-arrow-left-line" />
                  {t('back_to_home')}
                </a>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="space-y-6">
              {/* Share */}
              <div className="bg-background-100 border border-background-200/70 rounded-xl p-5">
                <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-4">{t('share')}</h4>
                <button
                  onClick={copyLink}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-700 hover:bg-background-200/50 transition-all"
                >
                  <i className="ri-link" />
                  {copied ? t('copied') : t('copy_link')}
                </button>
              </div>

              {/* Related Articles */}
              <div className="bg-background-100 border border-background-200/70 rounded-xl p-5">
                <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-4">{t('article_related')}</h4>
                <div className="space-y-3">
                  {related.map((ra) => (
                    <a
                      key={ra.id}
                      href={`/article/${ra.slug}`}
                      className="block group"
                    >
                      <h5 className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors mb-1">
                        {lang === 'pt' ? ra.titlePt : lang === 'de' ? ra.titleDe : ra.title}
                      </h5>
                      <p className="text-xs text-foreground-500 line-clamp-2">
                        {lang === 'pt' ? ra.descriptionPt : lang === 'de' ? ra.descriptionDe : ra.description}
                      </p>
                    </a>
                  ))}
                </div>
              </div>

              {/* All Articles */}
              <div className="bg-background-100 border border-background-200/70 rounded-xl p-5">
                <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-4">{t('nav_guides')}</h4>
                <div className="space-y-2">
                  {articles.slice(0, 6).map((a) => (
                    <a
                      key={a.id}
                      href={`/article/${a.slug}`}
                      className="block text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                    >
                      {lang === 'pt' ? a.titlePt : lang === 'de' ? a.titleDe : a.title}
                    </a>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block bg-primary-500 text-background-50 rounded-xl p-5 text-center hover:bg-primary-600 transition-colors"
              >
                <p className="font-heading text-sm font-semibold mb-1">{t('main_cta_button')}</p>
                <p className="text-xs text-background-200/80">kaspreadsheet.com</p>
              </a>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}